import { useEffect, useState } from "react";
import { useAuthContext } from "@app/authContext";
import { CustomerModel, fetchCustomerConfig, fetchCustomerModel, saveCustomerModel } from "@services/companyApi";
import { FormRootConfig } from "types/form";
import { FormProvider, useForm } from 'react-hook-form';
import { Button, message, Space, Tabs } from "antd";
import { CompanyContext } from "./CompanyTab";
import { CustomerQuestionnaire } from "./components/customerQuestionnaire";
import CustomerFinReport from "./components/CustomerFinReport";
import { DocumentsWrapper } from "@app/DocumentsBlock/documentsWrapper";

interface Props {
  customerId: string | null;
}

const footerStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 998, // Под всплывающим окном для физлиц (у которого 999)
  background: "#fff",
  padding: "0 16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderTop: "1px solid #f0f0f0",
  height: 68,
  boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
};

const contentStyle: React.CSSProperties = {
  marginBottom: 48,
}

export default function CustomerPage({ customerId }: Props) {
  const { auth } = useAuthContext();
  const accessToken = auth?.access_token ?? "";

  const methods = useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [model, setModel] = useState<CustomerModel | null>(null);
  const [config, setConfig] = useState<FormRootConfig | null>(null);

  useEffect(() => {
    if (!customerId) {
      setModel(null);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const model = await fetchCustomerModel(customerId, accessToken);
        setModel(model);
        const config = await fetchCustomerConfig(customerId, accessToken);
        setConfig(config);
        methods.reset(model.components)
      } catch (e) {
        console.warn('CustomerPage: не удалось получить модель:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [customerId]);

  if (!accessToken) return <div className="row muted">Нет подключения</div>;
  if (loading) return <div className="row muted">Загрузка данных...</div>;
  if (!model) return <div className="row muted">Не удалось анкету</div>;

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (!customerId) {
      return;
    }
    const payload = { components: { ...data } }
    try {
      setSaving(true);
      const newModel = await saveCustomerModel(customerId, payload, accessToken);
      setModel(newModel);
      methods.reset(newModel.components);
      BX24.closeApplication();
    } catch (err: any) {
      message.error(`Ошибка при сохранении анкеты: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  });

  const handleClose = () => BX24.closeApplication();

  const components = model.components;
  const inn = components?.customerCard?.inn ?? null;
  return (
    <CompanyContext.Provider value={{ formConfig: config, model, inn, customerId }}>
      <FormProvider {...methods}>
        <div>
          <div style={contentStyle}>
            <Tabs defaultActiveKey="1" items={[
              {
                key: "1",
                label: "Анкета клиента",
                children: <CustomerQuestionnaire />,
              },
              {
                key: "2",
                label: "Бухгалтерская отчётность",
                children: (
                  <>
                    {components?.customerFinReport && <CustomerFinReport prefix="customerFinReport" />}
                  </>
                ),
              },
              {
                key: "3",
                label: "Документы",
                children: <DocumentsWrapper prefix="customerDocuments" />,
              },
            ]} />
          </div>
          <div style={footerStyle}>
            <Space>
              <Button type="primary" size="large" onClick={handleSubmit} loading={saving} style={{ minWidth: 120, justifyContent: "center", }}>
                Сохранить
              </Button>
              <Button size="large" onClick={handleClose} style={{ minWidth: 120, justifyContent: "center", }}>
                Отмена
              </Button>
            </Space>
          </div>
        </div>
      </FormProvider>
    </CompanyContext.Provider>
  );
}
